import { Box, Stack, Typography } from '@mui/material';
import type { ColDef, SilobagRow } from '@siloreal:modules:private/silobag-list/config/extra/types';
import * as HOOKS from '@siloreal:shared:hooks';

const PLATFORM_GREEN = '#6baa00';
const PARTIAL_ORANGE = '#e8710a';
const FALLBACK_STRING = '—';

export function buildTotalExtractedWeightColumn(): ColDef {
  return {
    field: 'total_extracted_weight',
    headerName: HOOKS.useLocale.getState().t('temp.common.total_extracted'),
    minWidth: 132,
    renderCell: ({ row }) => <TotalExtractedWeightCell row={row} />,
  };
}

function TotalExtractedWeightCell({ row }: { row: SilobagRow }) {
  const hasExtraction = resolveHasExtraction(row);
  const weightLabel = formatExtractedWeight(row.total_extracted_weight);
  const accentColor = hasExtraction ? PARTIAL_ORANGE : PLATFORM_GREEN;

  const i18n = HOOKS.useLocale();

  return (
    <Stack
      height="100%"
      justifyContent="center"
      onClick={(event) => event.stopPropagation()}
      sx={{ minWidth: 0, px: 1, py: 0.5 }}
    >
      <Box
        title={
          hasExtraction
            ? `${i18n.t('temp.common.total_extracted')}: ${weightLabel}`
            : i18n.t('temp.common.no_extraction_registered')
        }
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.45,
          pl: 1,
          borderLeft: hasExtraction
            ? `3px solid ${colorToRgba(accentColor, 0.58)}`
            : `3px dashed ${colorToRgba('#a8a4a0', 0.35)}`,
          opacity: hasExtraction ? 1 : 0.42,
        }}
      >
        <Typography
          noWrap
          sx={{
            fontSize: 13,
            fontWeight: hasExtraction ? 600 : 500,
            lineHeight: 1.25,
            letterSpacing: '0.01em',
            color: hasExtraction ? '#3A3440' : '#a8a4a0',
            minWidth: 0,
          }}
        >
          {hasExtraction ? weightLabel : FALLBACK_STRING}
        </Typography>

        {hasExtraction ? (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.06em',
              lineHeight: 1.35,
              color: colorToRgba(PARTIAL_ORANGE, 0.88),
              textTransform: 'uppercase',
            }}
          >
            {i18n.t('temp.private.list.common.extracted')}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: '0.02em',
              lineHeight: 1.35,
              color: '#b8b4ad',
            }}
          >
            {i18n.t('temp.private.list.common.no_extractions')}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function resolveHasExtraction(row: SilobagRow): boolean {
  if (row.partially_extracted) return true;

  const extracted = Number(row.total_extracted_weight ?? 0);
  return Number.isFinite(extracted) && extracted > 0;
}

function formatExtractedWeight(value: string | number | null | undefined): string {
  const numericValue = Number(value ?? 0);

  if (!Number.isFinite(numericValue)) return FALLBACK_STRING;

  return `${numericValue.toLocaleString('es-AR')} kg`;
}

function colorToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;

  const red = parseInt(expanded.slice(0, 2), 16);
  const green = parseInt(expanded.slice(2, 4), 16);
  const blue = parseInt(expanded.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
